import { Features } from 'generated/featureDefinitions'
import { CompaniesLegalEntity, GetCompanyCurrenciesDocument } from 'generated/resources/graphql'
import compact from 'lodash/compact'
import uniq from 'lodash/uniq'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

type LegalEntity = Pick<CompaniesLegalEntity, 'id' | 'legalName' | 'main' | 'currency'>

export const useCompanyCurrency = () => {
  const { company } = useCurrentSession()

  const { data, error } = useQuery(GetCompanyCurrenciesDocument, {
    variables: {
      featureId: Features.finance_workspace_multicurrency,
    },
  })

  if (!data && !error) return { loading: true }
  if (!data || !company) return { loading: false }

  const supportedCurrencies = data?.finance.exchangeCurrenciesConnection.nodes.map(
    (currency) => currency.id
  )

  const companyCurrencies = compact(uniq(company.legalEntities.map((le) => le.currency)))
  const isMulticurrency = () => {
    return companyCurrencies.length >= 1
  }

  const mainLegalEntity = company.legalEntities.find((l) => l.main)

  let defaultSelectedLegalEntity: LegalEntity | undefined = undefined

  const unsupportedCurrencies = companyCurrencies.filter(
    (currency) => !supportedCurrencies.includes(currency)
  )

  // Determine if multicurrency features are available based on a combination
  // of feature availabilites and supported currencies
  const multicurrencyEnabled =
    (data.companies.featureAvailability?.available ?? false) && unsupportedCurrencies.length === 0

  if (!multicurrencyEnabled) {
    const userLegalEntity = company.legalEntities.find(
      (l) => l.id == data?.apiCore.currents[0]?.employee?.legalEntity?.id
    )
    if (userLegalEntity) {
      defaultSelectedLegalEntity = userLegalEntity
    } else {
      defaultSelectedLegalEntity = mainLegalEntity
    }
  }

  const defaultCurrency = defaultSelectedLegalEntity
    ? defaultSelectedLegalEntity.currency
    : mainLegalEntity?.currency

  return {
    data: {
      defaultSelectedLegalEntity,
      defaultCurrency,
      isMulticurrency: isMulticurrency(),
      multicurrencyEnabled,
    },
    loading: !data && !error,
  }
}
